import apiClient from "@/app/api/client";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import { organizationsApi } from "@/modules/organizations/api/organizations.api";
import { endpoints } from "@/shared/api/endpoints";
import { unwrapResponse } from "@/shared/utils/response";
import type { Building, BuildingTone } from "../types/objects.types";

type BackendBuilding = {
	address: null | string;
	availableCount?: number;
	blocksCount?: number;
	code: null | string;
	createdAt: string;
	district: null | string;
	id: string;
	image: null | string;
	isActive: boolean;
	maxFloorCount: number;
	name: string;
	number: null | number;
	orgId: string;
	showroomUrl: null | string;
	soldCount?: number;
	totalCount?: number;
	updatedAt: string;
};

type BackendResponse<T> = {
	success: true;
	data: T;
};

type BuildingsListResponse = {
	items: BackendBuilding[];
	meta: {
		limit: number;
		page: number;
		total: number;
		totalPages: number;
	};
};

export type BuildingCreatePayload = {
	address?: string;
	code?: string;
	district?: string;
	image?: string;
	name: string;
	number?: number;
	orgId?: string;
	showroomUrl?: string;
};

export type BuildingsListParams = {
	isActive?: "false" | "true";
	limit?: number;
	page?: number;
	search?: string;
	orgId?: string;
};

const tones: BuildingTone[] = ["blue", "green", "violet", "amber"];

const getInitials = (name: string) =>
	name
		.split(" ")
		.map((part) => part[0] || "")
		.join("")
		.slice(0, 2)
		.toUpperCase();

const getTone = (building: BackendBuilding): BuildingTone => {
	const seed = building.number ?? building.name.length;
	return tones[Math.abs(seed) % tones.length] ?? "blue";
};

const mapBuilding = (building: BackendBuilding): Building => ({
	id: building.id,
	name: building.name,
	code: building.code ?? "",
	initials: getInitials(building.name),
	tone: getTone(building),
	blocksCount: building.blocksCount ?? 0,
	soldCount: building.soldCount ?? 0,
	availableCount: building.availableCount ?? 0,
	totalCount: building.totalCount ?? 0,
	address: building.address ?? undefined,
	district: building.district ?? undefined,
	showroomUrl: building.showroomUrl ?? undefined,
	image: building.image ?? undefined,
	isActive: building.isActive,
	maxFloorCount: building.maxFloorCount,
	number: building.number ?? undefined,
	orgId: building.orgId,
});

/** Internal fetch helper — no Zustand dependency, pure HTTP + mapping */
async function fetchBuildingsRaw(params: BuildingsListParams): Promise<Building[]> {
	const response = await apiClient.get<
		BackendResponse<BuildingsListResponse> | BuildingsListResponse
	>(endpoints.objects.buildings, { params });
	const listResponse = unwrapResponse<BuildingsListResponse>(response.data);
	return listResponse.items.map(mapBuilding);
}

export const buildingsApi = {
	getBuildings: async (params: BuildingsListParams = {}): Promise<Building[]> => {
		const user = useAuthStore.getState().user;
		const isSuper = user?.role === "super";

		if (isSuper && !params.orgId) {
			const orgs = await organizationsApi.getOrganizations().catch(() => []);
			if (orgs.length === 0) {
				return [];
			}

			const buildingsArrays = await Promise.all(
				orgs.map((org) => fetchBuildingsRaw({ ...params, orgId: org.id }).catch(() => []))
			);
			return buildingsArrays.flat();
		}

		return fetchBuildingsRaw(params);
	},

	getBuilding: async (id: string): Promise<Building> => {
		const response = await apiClient
			.get<BackendResponse<BackendBuilding> | BackendBuilding>(endpoints.objects.buildingDetail(id))
			.catch((error) => {
				console.error("[BuildingsApi] Failed to fetch building:", error);
				throw error;
			});
		const building = unwrapResponse<BackendBuilding>(response.data);
		return mapBuilding(building);
	},

	createBuilding: async (payload: BuildingCreatePayload): Promise<Building> => {
		const response = await apiClient
			.post<BackendResponse<BackendBuilding> | BackendBuilding>(
				endpoints.objects.buildings,
				payload,
				payload.orgId ? { params: { orgId: payload.orgId } } : undefined
			)
			.catch((error) => {
				console.error("[BuildingsApi] Failed to create building:", error);
				throw error;
			});
		const building = unwrapResponse<BackendBuilding>(response.data);
		return mapBuilding(building);
	},

	updateBuilding: async (
		id: string,
		payload: Partial<BuildingCreatePayload> & { isActive?: boolean }
	): Promise<Building> => {
		const response = await apiClient
			.patch<BackendResponse<BackendBuilding> | BackendBuilding>(
				endpoints.objects.buildingDetail(id),
				payload
			)
			.catch((error) => {
				console.error("[BuildingsApi] Failed to update building:", error);
				throw error;
			});
		const building = unwrapResponse<BackendBuilding>(response.data);
		return mapBuilding(building);
	},

	deleteBuilding: async (id: string): Promise<void> => {
		await apiClient.delete(endpoints.objects.buildingDetail(id)).catch((error) => {
			console.error("[BuildingsApi] Failed to delete building:", error);
			throw error;
		});
	},
};
